// frontend/src/features/medications/adapters/medicationAdapter.js

function toNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const normalized = value.replace(",", ".").trim();
    if (!normalized) return null;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function pickFirstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function formatNumber(value, options = {}) {
  const numericValue = toNumber(value);

  if (numericValue === null) return null;

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(numericValue);
}

function normalizeMedicationType(type, item = {}) {
  const value = String(type || "").trim().toUpperCase();
  const form = String(item?.medicationForm || item?.medication_form || "").trim().toUpperCase();
  const name = String(item?.medicationName || item?.name || item?.medication_name || "").trim().toUpperCase();
  const unit = String(item?.unitLabel || item?.unit_label || "").trim().toUpperCase();

  if (
    value === "PO" ||
    value === "PÓ" ||
    value.includes("POWDER") ||
    value.includes("SUPLEMENTO") ||
    form.includes("PO") ||
    unit.includes("DOSE") ||
    name.includes("GLUTAMINA") ||
    name.includes("CREATINA") ||
    name.includes("WHEY") ||
    name.includes("COLAGENO") ||
    name.includes("COLÁGENO") ||
    name.includes("PROTEINA") ||
    name.includes("PROTEÍNA")
  ) {
    return "PO";
  }

  if (["INJETAVEL", "INJETÁVEL", "INJECTABLE"].includes(value)) {
    return "INJETAVEL";
  }

  if (["CAPSULA", "CÁPSULA", "CAPSULE"].includes(value)) {
    return "CAPSULA";
  }

  if (["LIQUIDO", "LÍQUIDO", "LIQUID"].includes(value)) {
    return "LIQUIDO";
  }

  if (["COMPRIMIDO", "TABLET", "PILL"].includes(value)) {
    return "COMPRIMIDO";
  }

  return "COMPRIMIDO";
}

function normalizeStatus(rawStatus) {
  const value = normalizeText(rawStatus);

  if (!value) return null;

  if (["acabou", "sem estoque", "esgotado"].includes(value)) return "acabou";
  if (["acabando", "baixo estoque"].includes(value)) return "acabando";
  if (["disponivel", "em estoque", "ativo", "em_uso", "em uso"].includes(value)) return "disponivel";
  if (["inativo"].includes(value)) return "inativo";

  return null;
}

function getMedicationName(item) {
  const raw = pickFirstDefined(
    item?.medicationName,
    item?.name,
    item?.medication_name
  ) || "Medicação sem nome";

  const norm = normalizeText(raw);
  if (norm.includes("tizerpatida") || norm.includes("tirzepatida")) {
    return "Tirzepatida";
  }
  if (norm.includes("semaglutida")) {
    return "Semaglutida";
  }
  if (norm.includes("liraglutida")) {
    return "Liraglutida";
  }
  if (norm.includes("glutamina")) {
    return "Glutamina";
  }
  return raw;
}

function getRawId(item) {
  return pickFirstDefined(
    item?.id,
    item?._id,
    item?.flaskId,
    item?.flask_id
  );
}

function getMedicationType(item) {
  return normalizeMedicationType(
    pickFirstDefined(item?.medicationType, item?.medication_type, item?.type),
    item
  );
}

function getInjectableAvailableMg(item) {
  const direct = pickFirstDefined(
    toNumber(item?.remaining_mg),
    toNumber(item?.remainingMg),
    toNumber(item?.availableMg),
    toNumber(item?.stockMg)
  );

  if (direct !== null && direct > 0) return direct;

  const totalMg = pickFirstDefined(
    toNumber(item?.total_mg_available),
    toNumber(item?.totalMgAvailable)
  );

  if (totalMg !== null && totalMg > 0 && normalizeStatus(item?.status) !== "acabou") {
    return totalMg;
  }

  return direct ?? 0;
}

function getUnitAvailable(item) {
  const direct = pickFirstDefined(
    toNumber(item?.remainingUnits),
    toNumber(item?.remaining_units),
    toNumber(item?.quantity),
    toNumber(item?.stockUnits)
  );

  if (direct !== null && direct > 0) return direct;

  const remMg = pickFirstDefined(
    toNumber(item?.remaining_mg),
    toNumber(item?.remainingMg)
  );

  const mgPerUnit = pickFirstDefined(
    toNumber(item?.mg_per_unit),
    toNumber(item?.mgPerUnit),
    toNumber(item?.mg_base),
    toNumber(item?.mgBase)
  );

  if (remMg !== null && remMg > 0) {
    if (mgPerUnit !== null && mgPerUnit > 0) {
      return Math.floor(remMg / mgPerUnit) || 1;
    }
    const totUnits = pickFirstDefined(
      toNumber(item?.total_units),
      toNumber(item?.totalUnits)
    );
    if (totUnits !== null && totUnits > 0) return totUnits;
    return 1;
  }

  const totalUnits = pickFirstDefined(
    toNumber(item?.total_units),
    toNumber(item?.totalUnits)
  );

  const status = normalizeStatus(item?.status);

  if (totalUnits !== null && totalUnits > 0 && status !== "acabou" && status !== "inativo") {
    return totalUnits;
  }

  const totalMg = pickFirstDefined(
    toNumber(item?.total_mg_available),
    toNumber(item?.totalMgAvailable)
  );

  if (totalMg !== null && totalMg > 0 && status !== "acabou" && status !== "inativo") {
    if (mgPerUnit !== null && mgPerUnit > 0) {
      return Math.floor(totalMg / mgPerUnit) || 1;
    }
    return totalUnits || 1;
  }

  return direct ?? 0;
}

function getMgPerUnit(item) {
  return pickFirstDefined(
    toNumber(item?.mgPerUnit),
    toNumber(item?.mg_per_unit),
    toNumber(item?.mg_base),
    toNumber(item?.mgBase),
    toNumber(item?.strengthMg)
  );
}

function buildGroupKey(item) {
  return `${normalizeText(getMedicationName(item))}::${getMedicationType(item)}`;
}

function resolveGroupedStatus(group) {
  if ((group.availableTotalNumeric ?? 0) <= 0) return "acabou";
  if (group.itemStatuses.includes("acabando")) return "acabando";
  if (group.itemStatuses.includes("disponivel")) return "disponivel";
  return "inativo";
}

function buildAvailableTotalLabel(group) {
  const value = formatNumber(group.availableTotalNumeric, {
    maximumFractionDigits: group.medicationType === "INJETAVEL" ? 2 : 0,
  });

  if (!value) return "—";

  if (group.medicationType === "INJETAVEL") {
    return `${value} mg`;
  }

  if (group.medicationType === "PO") {
    return `${value} doses`;
  }

  if (group.medicationType === "CAPSULA") {
    return `${value} cápsulas`;
  }

  return `${value} comprimidos`;
}

function buildSecondaryLabel(group) {
  if (group.medicationType === "INJETAVEL") {
    return "Dose: —";
  }

  if (group.medicationType === "PO") {
    return "Suplemento em pó";
  }

  const mg = formatNumber(group.mgPerUnit, { maximumFractionDigits: 2 });
  return mg ? `${mg} mg` : "—";
}

export function adaptMedicationList(rawItems) {
  if (!Array.isArray(rawItems)) return [];

  const groupedMap = new Map();

  rawItems.forEach((item) => {
    const key = buildGroupKey(item);
    const rawId = getRawId(item);
    const type = getMedicationType(item);
    const status = normalizeStatus(item?.status);

    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        id: key,
        name: getMedicationName(item),
        normalizedName: normalizeText(getMedicationName(item)),
        medicationType: type,

        packagingItems: [],
        packagingItemIds: [],
        itemStatuses: [],

        availableTotalNumeric: 0,
        mgPerUnit: null,
      });
    }

    const group = groupedMap.get(key);

    group.packagingItems.push(item);

    if (rawId) {
      group.packagingItemIds.push(String(rawId));
    }

    if (status) {
      group.itemStatuses.push(status);
    }

    if (type === "INJETAVEL") {
      const mg = getInjectableAvailableMg(item);
      if (mg !== null) group.availableTotalNumeric += mg;
    } else {
      const units = getUnitAvailable(item);
      if (units !== null) group.availableTotalNumeric += units;

      if (!group.mgPerUnit) {
        group.mgPerUnit = getMgPerUnit(item);
      }
    }
  });

  return Array.from(groupedMap.values()).map((group) => ({
    id: group.id,
    name: group.name,
    normalizedName: group.normalizedName,
    medicationType: group.medicationType,
    status: resolveGroupedStatus(group),

    availableTotal: group.availableTotalNumeric,
    availableTotalLabel: buildAvailableTotalLabel(group),

    secondaryLabel: buildSecondaryLabel(group),

    packagingItems: group.packagingItems,
    packagingItemIds: Array.from(new Set(group.packagingItemIds)),

    _debug: {
      packagingCount: group.packagingItems.length,
      ids: group.packagingItemIds,
    },
  }));
}